import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Printer, MapPin, MessageCircle, AlertCircle, CheckCircle2, ShieldCheck, Hash, Ban } from "lucide-react";

export default function MyRentals() {
  const { t, i18n } = useTranslation();
  const isEN = i18n.language === "en";
  const [bookings, setBookings] = useState(null);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    api.get("/bookings/mine").then((r) => setBookings(r.data)).catch(() => setBookings([]));
  }, []);

  const statusMap = {
    pagato: { label: t("rentals.status_pagato"), className: "bg-[#22c55e]/15 text-[#22c55e]", Icon: CheckCircle2 },
    in_attesa: { label: t("rentals.status_in_attesa"), className: "bg-[#FFD700]/15 text-[#FFD700]", Icon: AlertCircle },
    cancellato: { label: t("rentals.status_cancellato"), className: "bg-red-500/15 text-red-300", Icon: Ban },
  };

  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  // HTML escape user-controlled values before embedding in printable receipt
  // to prevent XSS via tampered backend fields (scooter_model, plate, email).
  const esc = (v) => String(v ?? "").replace(/[&<>"']/g, (c) => (
    { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
  ));

  const printReceipt = (b) => {
    const caparra = b.caparra_amount ?? b.total_amount * 0.25;
    const balance = b.balance_due_on_site ?? b.total_amount - caparra;
    const code = (b.id || "").slice(0, 8).toUpperCase();
    const win = window.open("", "_blank", "width=800,height=900");
    if (!win) return;
    const html = `<!DOCTYPE html>
<html lang="${isEN ? "en" : "it"}"><head><meta charset="utf-8" />
<title>${isEN ? "Booking confirmation" : "Conferma prenotazione"} #${esc(code)} · Rent Lupi</title>
<style>
  *{box-sizing:border-box}body{font-family:-apple-system,'Segoe UI',Roboto,sans-serif;color:#111;padding:40px;line-height:1.55;max-width:720px;margin:0 auto}
  .head{border-bottom:3px solid #FFD700;padding-bottom:18px;margin-bottom:24px}
  .brand{color:#FFD700;font-size:10px;letter-spacing:4px;text-transform:uppercase;font-weight:700}
  h1{margin:6px 0 0;font-size:28px}
  .grid{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin:18px 0}
  .cell{border:1px solid #e4e4e7;padding:10px 14px;border-radius:6px}
  .lbl{font-size:10px;color:#71717a;text-transform:uppercase;letter-spacing:1.5px}
  .val{font-size:15px;font-weight:600;margin-top:3px}
  .totals{background:#fafafa;border:1px solid #e4e4e7;padding:18px;border-radius:8px;margin:18px 0}
  .row{display:flex;justify-content:space-between;padding:6px 0;font-size:14px}
  .row.big{font-size:18px;font-weight:700;border-top:1px solid #d4d4d8;padding-top:10px;margin-top:6px;color:#16a34a}
  .row.balance{font-size:18px;font-weight:700;color:#b45309}
  .clause{background:#fef3c7;border-left:4px solid #f59e0b;padding:12px 16px;font-size:12.5px;margin:14px 0;border-radius:0 6px 6px 0;color:#78350f;line-height:1.5}
  .ferry{background:#fee2e2;border-left:4px solid #dc2626;padding:12px 16px;font-size:12.5px;color:#7f1d1d;border-radius:0 6px 6px 0;line-height:1.5}
  .foot{margin-top:32px;border-top:1px solid #e4e4e7;padding-top:14px;font-size:11px;color:#71717a;text-align:center}
  .code{font-family:monospace;color:#FFD700;font-weight:700;font-size:16px}
  @media print { body{padding:20px} }
</style></head><body>
  <div class="head">
    <div class="brand">Rent Lupi · Marciana Marina · Isola d'Elba</div>
    <h1>${isEN ? "Booking confirmation" : "Conferma prenotazione"}</h1>
    <div style="margin-top:6px;color:#71717a;font-size:13px">${isEN ? "Booking code" : "Codice prenotazione"}: <span class="code">#${esc(code)}</span></div>
  </div>
  <div class="grid">
    <div class="cell"><div class="lbl">${isEN ? "Vehicle" : "Veicolo"}</div><div class="val">${esc(b.scooter_model)}</div></div>
    <div class="cell"><div class="lbl">${isEN ? "Plate" : "Targa"}</div><div class="val">${esc((b.scooter_plate || "").toUpperCase())}</div></div>
    <div class="cell"><div class="lbl">${isEN ? "Pickup" : "Ritiro"}</div><div class="val">${esc(new Date(b.start_at).toLocaleString(isEN ? "en-GB" : "it-IT"))}</div></div>
    <div class="cell"><div class="lbl">${isEN ? "Return" : "Riconsegna"}</div><div class="val">${esc(new Date(b.end_at).toLocaleString(isEN ? "en-GB" : "it-IT"))}</div></div>
    <div class="cell"><div class="lbl">${isEN ? "Customer" : "Cliente"}</div><div class="val">${esc(b.user_email)}</div></div>
    <div class="cell"><div class="lbl">${isEN ? "Days" : "Giorni"}</div><div class="val">${esc(b.days || 0)}</div></div>
  </div>
  <div class="totals">
    <div class="row"><span>${isEN ? "Total rental" : "Totale noleggio"}</span><span>€${b.total_amount.toFixed(2)}</span></div>
    <div class="row big"><span>✓ ${isEN ? "Deposit paid (25%)" : "Caparra versata (25%)"}</span><span>€${caparra.toFixed(2)}</span></div>
    <div class="row balance"><span>${isEN ? "Balance due on-site" : "Saldo da versare in sede"}</span><span>€${balance.toFixed(2)}</span></div>
  </div>
  <div class="clause">
    <strong>${isEN ? "Cancellation policy" : "Politica di cancellazione"}:</strong>
    ${isEN
      ? "The deposit is fully and definitively retained in case of cancellation, no-show, or early return. Force majeure (weather, ferries, personal reasons) does not entitle to a refund. Art. 1385 Italian Civil Code."
      : "La caparra è trattenuta integralmente in caso di cancellazione, mancato ritiro o restituzione anticipata. Cause di forza maggiore (meteo, traghetti, motivi personali) non danno diritto al rimborso. Art. 1385 c.c."}
  </div>
  <div class="ferry">
    <strong>⚠ ${isEN ? "Important" : "Importante"}:</strong>
    ${isEN
      ? "The vehicle MUST remain on Elba island. Ferry transport is strictly forbidden."
      : "Il veicolo NON può lasciare l'Isola d'Elba. Il trasporto via traghetto è severamente vietato."}
  </div>
  <div style="margin-top:24px;padding:14px 16px;background:#f4f4f5;border-radius:8px">
    <strong>${isEN ? "Pickup point" : "Punto di ritiro"}:</strong> Rent Lupi · Marciana Marina · Isola d'Elba (LI)<br>
    <strong>Tel/WhatsApp:</strong> +39 0565 904134
  </div>
  <div class="foot">
    LUPI GIORGIO · P.IVA 00655790491 · C.F. LPUGRG62B14E931G · PEC lupigiorgio@pec.it · Località Caparutoli, 1 · 57033 Marciana Marina (LI)<br>
    ${isEN ? "This document constitutes proof of contract pursuant to Art. 50 et seq. of the Italian Consumer Code (D.Lgs. 206/2005)." : "Il presente documento costituisce prova del contratto ai sensi degli artt. 50 e ss. del Codice del Consumo (D.Lgs. 206/2005)."}
  </div>
  <script>window.onload=()=>{setTimeout(()=>window.print(),300)}</script>
</body></html>`;
    // Safe alternative to document.write: render through a Blob URL so the
    // browser parses the HTML in a clean document with no JS injection surface.
    const blobUrl = URL.createObjectURL(new Blob([html], { type: "text/html" }));
    win.location.href = blobUrl;
    // Release the URL after the new window has loaded the document.
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
  };

  if (bookings === null) return <div className="text-zinc-400">{t("common.loading")}</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-4xl font-bold tracking-tight">{t("rentals.title")}</h1>
        <p className="text-zinc-400 text-sm mt-2">
          {isEN
            ? "Your bookings are saved here. This page is the official confirmation channel: tap a booking to view the full details or print the receipt."
            : "Le tue prenotazioni sono salvate qui. Questa pagina è il canale ufficiale di conferma: tocca una prenotazione per il dettaglio completo o stampa la ricevuta."}
        </p>
      </div>

      {bookings.length === 0 ? (
        <div className="border border-zinc-800 rounded-xl p-10 text-center text-zinc-400">
          {t("rentals.empty")} <Link to="/catalog" className="text-[#FFD700] hover:underline">{t("rentals.explore")}</Link>.
        </div>
      ) : (
        <div className="space-y-3" data-testid="my-rentals-list">
          {bookings.map((b) => {
            const s = statusMap[b.payment_status] || statusMap.in_attesa;
            const StatusIcon = s.Icon;
            const isExpanded = expanded[b.id];
            const caparra = b.caparra_amount ?? b.total_amount * 0.25;
            const balance = b.balance_due_on_site ?? b.total_amount - caparra;
            const code = (b.id || "").slice(0, 8).toUpperCase();
            return (
              <div key={b.id} className="border border-zinc-800 rounded-xl bg-zinc-900/30 overflow-hidden" data-testid="rental-card">
                <button onClick={() => toggle(b.id)} className="w-full p-5 text-left hover:bg-zinc-900/50 transition flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`rental-toggle-${b.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-display font-bold text-lg">{b.scooter_model}</h3>
                      <Badge className={`${s.className} border-0 gap-1`}>
                        <StatusIcon className="w-3 h-3" />
                        {s.label}
                      </Badge>
                      <span className="inline-flex items-center gap-1 text-[10px] font-mono text-[#FFD700] bg-[#FFD700]/10 px-2 py-0.5 rounded border border-[#FFD700]/30">
                        <Hash className="w-2.5 h-2.5" />{code}
                      </span>
                    </div>
                    <p className="text-sm text-zinc-400 mt-1">
                      {new Date(b.start_at).toLocaleDateString(isEN ? "en-GB" : "it-IT")} → {new Date(b.end_at).toLocaleDateString(isEN ? "en-GB" : "it-IT")}
                    </p>
                    <p className="text-xs text-zinc-500 mt-1">{(b.scooter_plate || "").toUpperCase()} · {b.days || 0} {(b.days === 1) ? t("detail.day_one") : t("detail.day_other")}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <div className="text-xs text-zinc-500">{t("rentals.total")}</div>
                      <div className="font-display font-bold text-xl text-[#FFD700]">€{b.total_amount.toFixed(2)}</div>
                    </div>
                  </div>
                </button>

                {isExpanded && (
                  <div className="border-t border-zinc-800 p-5 space-y-4 bg-zinc-950/40" data-testid={`rental-detail-${b.id}`}>
                    {b.payment_status === "cancellato" && (
                      <div className="rounded-lg border border-red-500/40 bg-red-500/[0.08] p-4 flex gap-3" data-testid={`cancelled-banner-${b.id}`}>
                        <Ban className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                        <div className="text-sm text-red-100/90 leading-relaxed">
                          <strong className="text-red-300">
                            {isEN ? "Booking cancelled" : "Prenotazione annullata"}
                            {b.cancelled_at ? ` · ${new Date(b.cancelled_at).toLocaleDateString(isEN ? "en-GB" : "it-IT")}` : ""}
                          </strong>
                          <div className="mt-1 text-xs text-zinc-400">
                            {isEN
                              ? "This entry will be removed automatically 7 days after cancellation. Contact us if you have questions."
                              : "Questa voce verrà rimossa automaticamente 7 giorni dopo l'annullamento. Contattaci se hai dubbi."}
                          </div>
                        </div>
                      </div>
                    )}
                    {/* Price breakdown */}
                    <div className="rounded-lg border border-zinc-800 bg-zinc-900/60 p-4 space-y-2">
                      <h4 className="text-xs uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5 text-[#FFD700]" />{isEN ? "Payment details" : "Dettaglio pagamento"}</h4>
                      <div className="flex justify-between text-sm"><span className="text-zinc-400">{isEN ? "Total rental" : "Totale noleggio"}</span><span className="text-zinc-100">€{b.total_amount.toFixed(2)}</span></div>
                      <div className={`flex justify-between text-sm font-semibold ${b.payment_status === "pagato" ? "text-green-400" : "text-zinc-300"}`}>
                        <span>{b.payment_status === "pagato" ? "✓ " : ""}{isEN ? "Deposit (25%)" : "Caparra (25%)"}</span>
                        <span>€{caparra.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-base font-bold border-t border-zinc-800 pt-2 mt-2 text-amber-300">
                        <span>{isEN ? "Balance due on-site" : "Saldo in sede"}</span>
                        <span>€{balance.toFixed(2)}</span>
                      </div>
                      <p className="text-[11px] text-zinc-500 leading-relaxed pt-1">
                        {isEN
                          ? "Pay the balance in cash or by card when picking up the vehicle."
                          : "Il saldo si paga in contanti o con carta al ritiro del veicolo."}
                      </p>
                    </div>

                    {/* Cancellation reminder */}
                    <div className="rounded-lg border border-amber-500/30 bg-amber-500/[0.06] p-4 text-xs text-amber-100/90 leading-relaxed flex gap-2">
                      <AlertCircle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                      <div>
                        <strong className="text-amber-300">{isEN ? "Cancellation policy" : "Politica di cancellazione"}: </strong>
                        {isEN
                          ? "the deposit is non-refundable in case of cancellation, no-show or early return (Art. 1385 c.c.)."
                          : "la caparra è trattenuta integralmente in caso di cancellazione, mancato ritiro o restituzione anticipata (art. 1385 c.c.)."}
                      </div>
                    </div>

                    {/* Pickup address */}
                    <div className="rounded-lg border border-zinc-800 bg-zinc-900/60 p-4 text-sm">
                      <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-zinc-500 mb-1">
                        <MapPin className="w-3.5 h-3.5 text-[#FFD700]" /> {isEN ? "Pickup point" : "Punto di ritiro"}
                      </div>
                      <p className="text-zinc-200 leading-relaxed">Rent Lupi<br/>Marciana Marina · Isola d&apos;Elba (LI)</p>
                    </div>

                    {/* Home delivery info (only if requested) */}
                    {b.delivery_address && (
                      <div className="rounded-lg border border-[#FFD700]/40 bg-[#FFD700]/[0.06] p-4 text-sm" data-testid={`delivery-info-${b.id}`}>
                        <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#FFD700] mb-1">
                          <MapPin className="w-3.5 h-3.5" /> {isEN ? "Home delivery to" : "Consegna a domicilio a"}
                        </div>
                        <p className="text-zinc-100 leading-relaxed font-medium">{b.delivery_address}</p>
                        <p className="text-xs text-zinc-400 mt-2 leading-relaxed">
                          {isEN
                            ? "Deliveries happen at predefined slots. Please contact us on WhatsApp to agree on the exact time."
                            : "Le consegne avvengono ad orari predefiniti. Contattaci su WhatsApp per concordare l'orario esatto."}
                        </p>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex flex-wrap gap-2">
                      {b.payment_status === "pagato" ? (
                        <Button onClick={() => printReceipt(b)}
                                variant="outline"
                                className="bg-zinc-900 border-zinc-700 hover:bg-zinc-800 text-zinc-100"
                                data-testid={`rental-print-${b.id}`}>
                          <Printer className="w-4 h-4 mr-2" />
                          {isEN ? "Print receipt" : "Stampa ricevuta"}
                        </Button>
                      ) : (
                        <div className="flex items-center gap-2 text-xs text-zinc-500 px-3 py-2 rounded-md border border-zinc-800 bg-zinc-900/40" data-testid={`rental-print-locked-${b.id}`}>
                          <Printer className="w-4 h-4 opacity-50" />
                          {isEN
                            ? "Receipt available after deposit payment"
                            : "Stampa disponibile dopo il pagamento della caparra"}
                        </div>
                      )}
                      <a href="https://wa.me/390565904134" target="_blank" rel="noopener noreferrer">
                        <Button variant="outline"
                                className="bg-green-500/10 border-green-500/40 hover:bg-green-500/20 text-green-300"
                                data-testid={`rental-whatsapp-${b.id}`}>
                          <MessageCircle className="w-4 h-4 mr-2" />
                          {isEN ? "Contact us" : "Contattaci"}
                        </Button>
                      </a>
                      {b.payment_status === "in_attesa" && (
                        <Link to={`/checkout/${b.id}`}>
                          <Button size="sm" className="h-10 bg-[#FFD700] hover:bg-[#FACC15] text-black font-semibold" data-testid="resume-payment-button">
                            {t("rentals.pay")}
                          </Button>
                        </Link>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
